/**
 * What the model gateway page says about spend checks and spikes, decided away from the DOM
 * (`GET /api/v1/platform/spend-checks`, services/spend-check.ts).
 *
 * A spend check is run by a platform admin, from Rule42's AI costs page or over its MCP: a
 * provider and a period, the provider's figure against the cost the gateway recorded. This page
 * lists the latest runs and the kinds of call well above their usual share. Read defensively, as
 * discover-answer.ts reads its answer: a shape it cannot read is a failure the card says, never a
 * figure it makes up. The words match Rule42's (domain/ai-costs.ts there).
 *
 * @module router/web/pages/spend-check-view
 */

export type SpikeKind = 'client_closed' | 'estimated' | 'refused_upstream' | 'retries';

export interface Spike { kind: SpikeKind; calls: number; share: number; usualShare: number; factor: number | null }

export interface UnmatchedCall {
  requestId: string; generationId: string | null; orgId: string | null; at: string; recordedUsd: number; settlement: string | null;
}

export interface CheckRun {
  id: number;
  provider: string;
  from: string;
  to: string;
  figureSource: 'provider_api' | 'typed_opening' | 'typed';
  providerUsd: number;
  recordedUsd: number;
  gapUsd: number;
  gapPct: number | null;
  flagged: boolean;
  calls: number;
  estimatedCalls: number;
  unmatched: UnmatchedCall[];
  unmatchedMore: number;
  ranBy: { id: string; name: string | null };
  ranAt: string;
}

export interface SpendChecksView { runs: CheckRun[]; spikes: Spike[] }

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** The route's answer, or a thrown reason. */
export function readSpendChecks(body: unknown): SpendChecksView {
  const b = (body ?? {}) as Record<string, unknown>;
  const spikes = (b['spikes'] ?? null) as Record<string, unknown> | null;
  if (!Array.isArray(b['runs']) || !spikes || !Array.isArray(spikes['spikes'])) {
    throw new Error('The spend checks answered in a shape this page cannot read.');
  }
  return {
    runs: (b['runs'] as Array<Record<string, unknown>>).map((r) => ({
      id: num(r['id']) ?? 0,
      provider: String(r['provider'] ?? ''),
      from: String(r['from'] ?? ''),
      to: String(r['to'] ?? ''),
      figureSource: (r['figureSource'] === 'provider_api' || r['figureSource'] === 'typed_opening' ? r['figureSource'] : 'typed') as CheckRun['figureSource'],
      providerUsd: num(r['providerUsd']) ?? 0,
      recordedUsd: num(r['recordedUsd']) ?? 0,
      gapUsd: num(r['gapUsd']) ?? 0,
      gapPct: num(r['gapPct']),
      flagged: r['flagged'] === true,
      calls: num(r['calls']) ?? 0,
      estimatedCalls: num(r['estimatedCalls']) ?? 0,
      unmatched: Array.isArray(r['unmatched']) ? r['unmatched'] as UnmatchedCall[] : [],
      unmatchedMore: num(r['unmatchedMore']) ?? 0,
      ranBy: { id: String((r['ranBy'] as Record<string, unknown> | undefined)?.['id'] ?? ''), name: ((r['ranBy'] as Record<string, unknown> | undefined)?.['name'] as string | null) ?? null },
      ranAt: String(r['ranAt'] ?? ''),
    })),
    spikes: spikes['spikes'] as Spike[],
  };
}

export const usd = (n: number): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;

/** "28 Oct, 19:17", in UTC so two admins read the same instant. */
export function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}, ${d.toISOString().slice(11, 16)} UTC`;
}

/** Each watched kind as the end of "15% of calls ...", as Rule42's page says it. */
const SPIKE_TITLES: Record<SpikeKind, string> = {
  client_closed: 'stopped before the answer finished',
  estimated: 'had their cost estimated',
  refused_upstream: 'were refused by the provider',
  retries: 'were a third try or later',
};

/** "15% of calls stopped before the answer finished in the last 24 hours, usually 5%". */
export function spikeLine(s: Spike): string {
  const pct = (n: number): string => `${Math.round(n * 100)}%`;
  return `${pct(s.share)} of calls ${SPIKE_TITLES[s.kind] ?? `were ${s.kind}`} in the last 24 hours, usually ${pct(s.usualShare)}`;
}

/** A gap's share: one decimal under 10% ("0.4%"), whole above ("51%"). */
export function gapPctWords(gapPct: number): string {
  const v = Math.abs(gapPct);
  return v < 10 ? `${Number(v.toFixed(1))}%` : `${Math.round(v)}%`;
}

/** The built-in providers' names (services/source-provider-store.ts); any other reads as its id. */
const PROVIDER_NAMES: Record<string, string> = {
  openrouter: 'OpenRouter', anthropic: 'Anthropic', openai: 'OpenAI', deepseek: 'DeepSeek', 'z-ai': 'Z.ai',
  'x-ai': 'xAI', mistral: 'Mistral', moonshot: 'Moonshot', google: 'Google',
};
export const providerName = (id: string): string => PROVIDER_NAMES[id] ?? id;

/** A run in one line: the provider's figure against what was recorded, and the gap. */
export function runLine(r: CheckRun): string {
  const both = `${providerName(r.provider)} billed ${usd(r.providerUsd)}, ${usd(r.recordedUsd)} recorded.`;
  const share = r.gapPct !== null ? ` (${gapPctWords(r.gapPct)})` : '';
  if (!r.flagged) return `Adds up. ${both}`;
  if (r.gapUsd > 0) return `${usd(r.gapUsd)} not recorded${share}. ${both}`;
  return `${usd(-r.gapUsd)} more recorded than billed${share}. ${both}`;
}

/** The badge: whether the run's figures add up. */
export function runBadge(r: Pick<CheckRun, 'flagged'>): { variant: 'success' | 'error'; label: string } {
  return r.flagged ? { variant: 'error', label: "Doesn't add up" } : { variant: 'success', label: 'Adds up' };
}

/** Where the provider's figure came from, in words. */
export function figureWords(source: CheckRun['figureSource']): string {
  if (source === 'provider_api') return "both totals read from the provider's account";
  if (source === 'typed_opening') return "a starting total typed in, the ending total read from the provider's account";
  return "typed in from the provider's usage page";
}
